import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import {
  ExternalShipmentTracker,
  Trackable,
  TrackableFulfillment,
} from "@redotech/redo-model/trackable";
import AlertCircleIcon from "@redotech/redo-web/arbiter-icon/alert-circle.svg";
import { Flex } from "@redotech/redo-web/flex";
import { TrackableStatus } from "@redotech/redo-web/settings-elements/order/trackable-status";
import { Header, Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import {
  ExceptionalStatus,
  exceptionalStatusVerbiage,
} from "../shipment-exceptions";
import * as fulfillmentCss from "./fulfillment.module.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const FULFILLMENT_SHIPMENT_STATUS_MAP: Record<string, string | undefined> = {
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  unknown: "Unknown",
};

const FULFILLMENT_STATUS_MAP: Record<string, string | undefined> = {
  success: "Fulfilled",
  failure: "Failure",
  pending: "Pending",
  unknown: "Unknown",
  cancelled: "Cancelled",
  error: "Error",
  open: "Open",
};

export const StatusDisplay = memo(function StatusDisplay({
  exceptionalStatus,
  pastPreTransit,
  delivery,
  trackable,
  fulfillment,
  pickupConfig,
  settings,
  merchantName,
  robustTracker,
}: {
  exceptionalStatus: any;
  pastPreTransit: boolean;
  delivery: { isDelivered: boolean; date: Date | null };
  trackable: Trackable;
  fulfillment?: TrackableFulfillment;
  pickupConfig: any;
  settings: ReturnAppSettings;
  merchantName: string;
  robustTracker?: ExternalShipmentTracker;
}) {
  const renderDeliveryDate = () => {
    if (!pastPreTransit || delivery.isDelivered || !delivery.date) return null;

    return (
      <>
        <Text fontSize="md" fontWeight="semibold">
          {DAYS_OF_WEEK[delivery.date.getUTCDay()]},{" "}
          {MONTHS[delivery.date.getUTCMonth()]}
        </Text>
        <Text className={fulfillmentCss.deliveryDate} fontWeight="regular">
          {delivery.date.getUTCDate() ?? "Unknown"}
        </Text>
      </>
    );
  };

  const fulfillmentStatus = useMemo(() => {
    if (fulfillment?.shipment_status) {
      return FULFILLMENT_SHIPMENT_STATUS_MAP[fulfillment.shipment_status];
    } else if (fulfillment?.status) {
      return FULFILLMENT_STATUS_MAP[fulfillment.status];
    }
    return "unknown";
  }, [fulfillment]);

  const renderStatus = () => {
    if (robustTracker) {
      return (
        <>
          <Text fontSize="md" fontWeight="semibold">
            Status
          </Text>
          <Flex justify="space-between">
            <TrackableStatus
              asHeader
              fulfillment={fulfillment}
              pickupConfig={pickupConfig}
              source={trackable.originalOrder.shopify.sourceName}
              status={robustTracker?.status || "unknown"}
              storeUrl={settings.storeUrl}
              trackable={trackable}
            />
          </Flex>
        </>
      );
    }

    if (fulfillment) {
      return (
        <>
          <Text fontSize="md" fontWeight="semibold">
            Status
          </Text>
          <Header
            fontSize="sm"
            fontWeight="medium"
            mb="md"
            mt="md"
            textColor="secondary"
          >
            {fulfillmentStatus}
          </Header>
        </>
      );
    }

    return (
      <Text fontSize="xl" fontWeight="bold">
        Your order is being prepared
      </Text>
    );
  };

  if (exceptionalStatus) {
    return (
      <Flex dir="column" mt="md">
        <Flex
          alignSelf="flex-start"
          bgColor="error-secondary"
          className={fulfillmentCss.errorIcon}
          radius="full"
          textColor="error"
        >
          <AlertCircleIcon height="20" width="20" />
        </Flex>
        <TrackableStatus
          fulfillment={fulfillment}
          pickupConfig={pickupConfig}
          source={trackable.originalOrder.shopify.sourceName}
          status={exceptionalStatus.status || "unknown"}
          storeUrl={settings.storeUrl}
          trackable={trackable}
        />
        <Flex>
          <Text fontSize="sm">
            {exceptionalStatusVerbiage[
              exceptionalStatus.status as ExceptionalStatus
            ](merchantName)}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      {renderDeliveryDate()}
      {renderStatus()}
    </>
  );
});
